import { useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { Panel, btn } from "@/components/ui-kit";

export const SettingsBackupPanel = ({ onRestored }) => {
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const exportFile = async () => {
    try {
      const r = await api.get("/settings/export", { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "EP_Partner_Settings.json";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = name;
      a.click();
      toast.success("Settings backup downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const data = JSON.parse(await f.text());
      if (data.kind !== "partner-settings") throw new Error("Not an EP Partner Settings backup file");
      setPending({ name: f.name, data });
    } catch (err) {
      toast.error(err.message || "Could not read file");
    }
  };
  const restore = async () => {
    try {
      const r = (await api.post("/settings/import", pending.data)).data;
      onRestored(r.settings);
      toast.success(`Restored ${r.settings_restored} settings and ${r.sites_restored} sites`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setPending(null);
  };
  return (
    <Panel title="Settings backup" testId="settings-backup-panel">
      <div className="flex flex-wrap items-center gap-3 p-5">
        <Button onClick={exportFile} className={btn.primary} data-testid="settings-export-btn"><Download className="mr-1.5 h-4 w-4" /> Export settings</Button>
        <Button onClick={() => fileRef.current?.click()} variant="ghost" className={btn.ghost} data-testid="settings-import-btn"><Upload className="mr-1.5 h-4 w-4" /> Restore from file</Button>
        <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={pick} data-testid="settings-import-input" />
        <span className="text-xs text-slate-500">Saves all Partner Settings and xDock site details to a .json file. Passwords and secrets (SFTP password, Coupa secret) and AS2 certificates are not included and stay as they are.</span>
      </div>
      <AlertDialog open={!!pending} onOpenChange={(v) => !v && setPending(null)}>
        <AlertDialogContent data-testid="settings-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore settings?</AlertDialogTitle>
            <AlertDialogDescription>
              Replace the current Partner Settings with {pending?.name} (exported {pending?.data?.exported_at ? new Date(pending.data.exported_at).toLocaleString("en-GB") : "unknown"}). A copy of the current settings is kept on the server first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={restore} data-testid="settings-restore-confirm">Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
